import {
  createVListFromRegion,
  findClosestElementAncestor,
  getRegionsFromRange,
  Position,
  VList,
} from 'roosterjs-editor-dom';
import { Indentation, ListType as VListType, Region, RegionType } from 'roosterjs-editor-types';
import { getElementsIntersectingRange } from '../dom/element';
import { isListItemElement, ListType } from '../dom/list';

/*
 * Creating a vList.
 */

export const createVListFromNode = (
  node: HTMLElement,
  container: HTMLElement,
  range: Range,
): VList | null => {
  const region = computeRegionFromRange(container, range);
  if (!region) {
    return null;
  }

  // `createVListFromRegion()` ignores `includeSiblingLists` when called with `startNode`.
  return createVListFromRegion(region, undefined, node);
};

/*
 * List Edit operations.
 */

export const changeListIndentInRange = (
  vList: VList,
  range: Range,
  type: Indentation,
  softOutdent: boolean = false,
) => {
  const { startPosition, endPosition } = getPositionsFromRange(range);

  if (type === Indentation.Decrease) {
    vList.setIndentation(startPosition, endPosition, type, softOutdent);
  } else {
    vList.setIndentation(startPosition, endPosition, type);
  }
  vList.writeBack();
};

export const toggleListTypeInRange = (vList: VList, range: Range, type: ListType) => {
  const { startPosition, endPosition } = getPositionsFromRange(range);

  const listType = type === ListType.ORDERED ? VListType.Ordered : VListType.Unordered;

  // Change list type will outdent the item if the listType is the same as
  // the closest list element wrapping the item.
  vList.changeListType(startPosition, endPosition, listType);
  vList.writeBack();
};

/*
 * Helpers.
 */

const getPositionsFromRange = (range: Range) => ({
  startPosition: Position.getStart(range),
  endPosition: Position.getEnd(range),
});

const computeRegionFromRange = (container: HTMLElement, range: Range): Region | null => {
  // This RegionType.Table is a required parameter (and the only option here)
  // that basically specifies that we will break regions on table elements. We don't
  // care about that use case when computing a range for lists, so if we do get
  // multiple regions here we just return null
  const regions = getRegionsFromRange(container, range, RegionType.Table);

  // We don't want to allow list creation across non-contiguous regions
  if (regions.length !== 1) return null;

  return regions[0];
};

/**
 * This operation is stateful and can be thought of as opening a transaction
 * that must be closed. To do so, call `writeBack()` on all returned `VList`s.
 */
export const parseVLists = (range: Range, container: HTMLElement): VList[] => {
  const listItems = getElementsIntersectingRange(container, range, isListItemElement);
  const vLists: VList[] = [];

  for (const listItem of listItems) {
    // Check to see if this item is contained in one of the lists we've
    // already created, skip if it is.
    if (!vLists.some(vList => vList.contains(listItem))) {
      const newVList = createVListFromNode(listItem, container, range);

      if (newVList && newVList.items.length > 0) {
        vLists.push(newVList);
      }
    }
  }

  return vLists;
};

/**
 * Determine which, if any, unique list type this range contains.
 */
export const findSingleListType = (container: Element, range: Range): ListType | undefined => {
  let type;

  for (const items of getElementsIntersectingRange(container, range, isListItemElement)) {
    const list = findClosestElementAncestor(items, container, 'ul,ol');

    if (!list) {
      continue;
    }

    if (!type) {
      type = list.tagName;
    } else if (type !== list.tagName) {
      return undefined;
    }
  }

  if (!type) {
    return undefined;
  }

  return type === 'UL' ? ListType.UNORDERED : ListType.ORDERED;
};
